/**
 * The small sums behind the hub's doorway tiles: the dataset's labels, the groups, the configs,
 * and the name of each build the Configuración tile shows.
 *
 * Each tile answers one question in a glance and sends the rest to its screen, so what it shows is
 * a count or a split and never a list to read. The counts are taken from data the screens already
 * fetch, with nothing asked of Virtual Profiler for the hub alone: a figure here that disagreed
 * with the screen behind the tile would make one of the two a incoherent.
 */
import type {
  AnalysisConfigSummary,
  AnalysisGroup,
  AnalysisRun,
  RunStatus,
  CoherenceMetrics,
} from '../model.js';

/**
 * The states a run is still moving through.
 *
 * The same four the live-runs query asks for. A cancellation that was only requested is still in
 * flight: cancelling is cooperative, and the run keeps working until its next checkpoint.
 */
export const IN_FLIGHT_STATUSES: readonly RunStatus[] = Object.freeze([
  'QUEUED',
  'DISPATCHED',
  'RUNNING',
  'CANCEL_REQUESTED',
]);

// ── The labels of the dataset ───────────────────────────────────────────────

export interface LabelSegment {
  key: 'truthful' | 'incoherence' | 'unlabelled';
  label: 'Verdad' | 'Mentira' | 'Sin etiquetar';
  /** Pinned: «Verdad» is always success and «Mentira» always danger, everywhere. */
  tone: 'success' | 'danger' | 'neutral';
  count: number;
  /** Whole percent. The three always add up to 100, or are all 0 when there is nothing. */
  pct: number;
}

type LabelCorpus = Pick<CoherenceMetrics['corpus'], 'total' | 'labelled' | 'withIncoherence' | 'truthful'>;

/**
 * Whole percentages that add up to exactly 100: the largest-remainder method.
 *
 * Rounding each share on its own gives 33 + 33 + 33 for three equal parts, and a bar whose legend
 * adds to 99 reads as a missing video. The points left over go to the largest remainders; on a
 * tie, to the one listed first, so the answer does not depend on how a sort breaks ties.
 */
function wholeShares(counts: readonly number[]): number[] {
  const sum = counts.reduce((total, count) => total + count, 0);
  if (sum <= 0) return counts.map(() => 0);

  // Integer arithmetic throughout: comparing floating remainders would decide ties by rounding
  // noise.
  const floors = counts.map((count) => Math.floor((count * 100) / sum));
  const remainders = counts.map((count) => (count * 100) % sum);
  let left = 100 - floors.reduce((total, floor) => total + floor, 0);

  const order = counts
    .map((_, index) => index)
    .sort((a, b) => (remainders[b] as number) - (remainders[a] as number) || a - b);

  for (const index of order) {
    if (left <= 0) break;
    floors[index] = (floors[index] as number) + 1;
    left -= 1;
  }

  return floors;
}

/**
 * How the dataset splits by label: Verdad, Mentira and Sin etiquetar, always in that order.
 *
 * The corpus counts only the videos that enter the metrics, so the three may not add up to the
 * dataset's total; the tile says so beside it. Unlabelled is what `total` has beyond `labelled`,
 * and never negative: an upstream answer where the two disagree the other way must not draw a
 * segment of negative width.
 */
export function labelSplit(corpus: LabelCorpus | null | undefined): {
  empty: boolean;
  segments: LabelSegment[];
} {
  const truthful = corpus?.truthful ?? 0;
  const withIncoherence = corpus?.withIncoherence ?? 0;
  const unlabelled = corpus ? Math.max(0, corpus.total - corpus.labelled) : 0;
  const [truthfulPct, incoherencePct, unlabelledPct] = wholeShares([truthful, withIncoherence, unlabelled]) as [
    number,
    number,
    number,
  ];

  return {
    empty: truthful + withIncoherence + unlabelled <= 0,
    segments: [
      { key: 'truthful', label: 'Verdad', tone: 'success', count: truthful, pct: truthfulPct },
      { key: 'incoherence', label: 'Mentira', tone: 'danger', count: withIncoherence, pct: incoherencePct },
      {
        key: 'unlabelled',
        label: 'Sin etiquetar',
        tone: 'neutral',
        count: unlabelled,
        pct: unlabelledPct,
      },
    ],
  };
}

/**
 * How many videos are being analysed right now.
 *
 * Videos, not runs: a video re-launched while its previous run is still in the queue is one video
 * in analysis, and counting it twice would make the tile disagree with the gallery's spinners.
 * Only manual analyses are videos; interviews and CVs share the ledger but not the dataset.
 */
export function analysingVideos(
  runs: readonly Pick<AnalysisRun, 'sourceType' | 'sourceId'>[] | null | undefined,
): number {
  const videos = new Set<string>();

  for (const run of runs ?? []) {
    if (run.sourceType === 'manual_analysis') videos.add(run.sourceId);
  }

  return videos.size;
}

// ── The groups ──────────────────────────────────────────────────────────────

function countOf(group: Pick<AnalysisGroup, 'runsByStatus'>, status: RunStatus): number {
  return group.runsByStatus[status] ?? 0;
}

/** Runs of the group still moving, every attempt included. */
export function groupInFlight(group: Pick<AnalysisGroup, 'runsByStatus'>): number {
  return IN_FLIGHT_STATUSES.reduce((total, status) => total + countOf(group, status), 0);
}

/**
 * The group's runs as the table's «Ejecuciones» cell reads them.
 *
 * Cancelled runs are in none of the three: nobody acts on them, and a fourth figure in a cell
 * this narrow would push the ones somebody does act on off the line.
 */
export function groupRunCounts(group: Pick<AnalysisGroup, 'runsByStatus'>): {
  ok: number;
  failed: number;
  inFlight: number;
} {
  return {
    ok: countOf(group, 'SUCCEEDED'),
    failed: countOf(group, 'FAILED'),
    inFlight: groupInFlight(group),
  };
}

/** An instant as milliseconds, or null when there is none worth sorting by. */
function instant(value: Date | null | undefined): number | null {
  if (!value) return null;

  const ms = value.getTime();

  // An invalid date would make the comparison NaN, and a sort fed NaN has no defined order.
  return Number.isFinite(ms) ? ms : null;
}

/** Newest first, with nothing last. */
function newestFirst(a: Date | null | undefined, b: Date | null | undefined): number {
  const left = instant(a);
  const right = instant(b);

  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;

  return right - left;
}

/**
 * The groups in the order somebody arriving at the hub wants them.
 *
 * What is running first, because it is what is about to change. Then the most recent
 * measurement, because it is the last thing there is to look at. The groups that never measured
 * anything come after every group that did, and among themselves the most recently touched goes
 * first, so the one created a minute ago is not under the one abandoned a year ago. A copy, stable
 * on ties, so the list the view holds keeps its own order.
 */
export function groupsByActivity(groups: readonly AnalysisGroup[]): AnalysisGroup[] {
  return [...groups].sort(
    (a, b) =>
      Number(groupInFlight(b) > 0) - Number(groupInFlight(a) > 0) ||
      newestFirst(a.lastMeasurement?.computedAt, b.lastMeasurement?.computedAt) ||
      newestFirst(a.updatedAt, b.updatedAt),
  );
}

/**
 * The chips above the groups table.
 *
 * The hub reads one page of groups, so a count taken from it is a floor when the page is not all
 * of them. `partial` is how the chips say «at least»: «3 en marcha» over a page of fifty out of
 * eighty would be a claim about thirty groups nobody read.
 */
export function groupsDigest(
  groups: readonly AnalysisGroup[],
  total: number | null,
): { total: number; inFlight: number; unmeasured: number; outdated: number; partial: boolean } {
  return {
    total: total ?? groups.length,
    inFlight: groups.filter((group) => groupInFlight(group) > 0).length,
    // Null and not «0 %»: a group that measured nothing has not scored zero.
    unmeasured: groups.filter((group) => group.lastMeasurement === null).length,
    // «Desactualizado», as upstream worked it out: unknown from an older one, so not counted.
    outdated: groups.filter((group) => group.reanalysis?.status === 'outdated').length,
    partial: total !== null && total > groups.length,
  };
}

// ── The configurations ──────────────────────────────────────────────────────

/**
 * The configurations in three facts: how many, how many can launch an analysis, the newest version.
 *
 * Whether one can launch is upstream's `runnable`, never guessed from its version here. A
 * configuration never saved with engines has no version, so it counts in the total and does not
 * move the highest. With no version anywhere the highest is null, not 0: «v0» would name a
 * version that does not exist.
 */
export function configsDigest(
  configs: readonly Pick<AnalysisConfigSummary, 'runnable' | 'version'>[] | null | undefined,
): { total: number; runnable: number; highestVersion: number | null } {
  const list = configs ?? [];
  let highestVersion: number | null = null;

  for (const config of list) {
    if (config.version !== null && (highestVersion === null || config.version > highestVersion)) {
      highestVersion = config.version;
    }
  }

  return {
    total: list.length,
    runnable: list.filter((config) => config.runnable).length,
    highestVersion,
  };
}

// ── The builds ──────────────────────────────────────────────────────────────

/**
 * «worker 2.3.1 · 9f2c1a0». The build is named by its first seven characters, as a commit is
 * everywhere else, and left out when upstream did not say which build it is.
 */
export function buildLabel(name: string, version: string, sha: string | null | undefined): string {
  return sha ? `${name} ${version} · ${sha.slice(0, 7)}` : `${name} ${version}`;
}
